import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import {
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
} from '../../features/api/apiSlice';

/**
 * Header bell + dropdown for user/agent in-app notifications.
 * @param {{ accent?: 'sky' | 'amber', inboxPath?: string }} props
 */
const NotificationBell = ({ accent = 'sky', inboxPath = '/notifications' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const { data: countData, refetch: refetchCount } = useGetNotificationUnreadCountQuery(undefined, {
    pollingInterval: 60000,
  });
  const { data: listData, isLoading, refetch: refetchList } = useGetNotificationsQuery(
    { limit: 8, offset: 0 },
    { skip: !open, pollingInterval: open ? 30000 : 0 }
  );
  const [markRead] = useMarkNotificationsReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllNotificationsReadMutation();

  const unread = countData?.unread_count ?? 0;
  const items = listData?.data || [];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const accentClasses =
    accent === 'amber'
      ? {
          badge: 'bg-amber-500',
          hover: 'hover:bg-amber-50',
          link: 'text-amber-700 hover:text-amber-900',
          dot: 'bg-amber-500',
        }
      : {
          badge: 'bg-sky-500',
          hover: 'hover:bg-sky-50',
          link: 'text-sky-700 hover:text-sky-900',
          dot: 'bg-sky-500',
        };

  const actionIcon = (type) => {
    switch (type) {
      case 'property_need':
        return 'ClipboardList';
      case 'need_response':
      case 'response_status':
        return 'MessageSquare';
      case 'message':
        return 'Mail';
      case 'property':
        return 'Building2';
      default:
        return 'Bell';
    }
  };

  const handleOpenItem = async (item) => {
    if (!item.is_read) {
      try {
        await markRead({ ids: [item.id] }).unwrap();
        refetchCount();
        refetchList();
      } catch {
        // still navigate
      }
    }
    setOpen(false);
    if (item.link) {
      navigate(item.link);
    } else {
      navigate(inboxPath);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead().unwrap();
      refetchCount();
      refetchList();
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) {
            refetchList();
            refetchCount();
          }
        }}
        className="relative p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        aria-label="Notifications"
        title="Notifications"
      >
        <Icon name="Bell" size={18} />
        {unread > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full ${accentClasses.badge} text-white text-[10px] font-bold flex items-center justify-center`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(100vw-2rem,22rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                disabled={markingAll}
                className={`text-xs font-medium ${accentClasses.link}`}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isLoading && (
              <div className="px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Icon name="BellOff" size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            )}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleOpenItem(item)}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-50 ${accentClasses.hover} ${
                  !item.is_read ? 'bg-slate-50/80' : ''
                }`}
              >
                <div className="mt-0.5 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon name={actionIcon(item.action_type)} size={14} className="text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <p
                      className={`text-sm text-slate-900 truncate flex-1 ${
                        !item.is_read ? 'font-semibold' : 'font-medium'
                      }`}
                    >
                      {item.title}
                    </p>
                    {!item.is_read && (
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${accentClasses.dot}`} />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{item.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{item.created}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50">
            <Link
              to={inboxPath}
              onClick={() => setOpen(false)}
              className={`text-xs font-semibold ${accentClasses.link}`}
            >
              View all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
